import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import { getHeaderTheme } from "../../../config/headerTheme";
import { useAcademyCourses } from "../hooks/useAcademyCourses";
import { useAcademyProgress } from "../hooks/useAcademyProgress";
import { useAcademyCourseDetail } from "../hooks/useAcademyCourseDetail";
import { useCompleteVideo } from "../hooks/useCompleteVideo";
import { useEnrollCourse } from "../hooks/useEnrollCourse";
import AcademyAPI from "../services/academyService";
import { addTenantTopic } from "../../quiz/services/tenantTopicsService";
import { getTenantId } from "../../../utils/tenant";
import { useUser } from "../../../hooks/useUser";
import { useDeviceType } from "../../../hooks/useMediaQuery";
import playCircleIcon from "../../../assets/play-circle.png";
import QuizCategoryFilter from "../../quiz/components/QuizCategoryFilter";

const getYoutubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
};

const theme = getHeaderTheme();

const AcademyHome = () => {
    const [view, setView] = useState("list"); // 'list' | 'detail'
    const [selectedCourseId, setSelectedCourseId] = useState(null);
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [showBackButton, setShowBackButton] = useState(false);
    const [showNextButton, setShowNextButton] = useState(false);
    const [redeemingTrivia, setRedeemingTrivia] = useState(false);
    const [enrollingCourseId, setEnrollingCourseId] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [courseVideoCounts, setCourseVideoCounts] = useState({});
    const [selectedVideoIndex, setSelectedVideoIndex] = useState(0);
    const videoRef = useRef(null);
    const youtubePlayerRef = useRef(null);
    const handleVideoEndedRef = useRef(() => {});

    const { courses, loading: loadingCourses, error: errorCourses, reload: reloadCourses } = useAcademyCourses();
    const { progress, loading: loadingProgress, error: errorProgress, reload: reloadProgress } = useAcademyProgress();
    const { course: courseDetail, loading: loadingDetail, error: errorDetail } = useAcademyCourseDetail({
        courseId: selectedCourseId,
        enabled: view === "detail",
    });
    const routerHistory = useHistory();
    const { isHost, isOwner } = useUser();
    const { isMobile, isDesktop } = useDeviceType();

    const { complete, loading: completing } = useCompleteVideo();
    const { enroll } = useEnrollCourse();

    const progressByCourseId = useMemo(() => {
        const map = {};
        progress.forEach((p) => {
            const cid = typeof p.courseId === "object" ? p.courseId?._id : p.courseId;
            if (cid) map[cid] = p;
        });
        return map;
    }, [progress]);

    const enrolledCourseIds = useMemo(() => {
        const ids = new Set();
        progress.forEach((p) => {
            const cid = typeof p.courseId === "object" ? p.courseId?._id : p.courseId;
            if (cid) ids.add(String(cid));
        });
        return ids;
    }, [progress]);

    const categoryOptions = useMemo(
        () => Array.from(new Set(courses.map((c) => c.category?.name || c.category).filter(Boolean))),
        [courses]
    );

    const allCourses = useMemo(() => {
        return courses
            .filter((course) => !selectedCategory || (course.category?.name || course.category) === selectedCategory)
            .map((course) => {
                const p = progressByCourseId[course._id];
                return { course, progress: p, percentage: p?.percentage || 0 };
            });
    }, [courses, progressByCourseId, selectedCategory]);

    const watchedCourses = useMemo(
        () => allCourses.filter((item) => item.percentage > 0),
        [allCourses]
    );
    const notWatchedCourses = useMemo(
        () => allCourses.filter((item) => item.percentage === 0),
        [allCourses]
    );

    const courseVideos = useMemo(
        () => (courseDetail?.videos || []).filter((video) => video.active !== false),
        [courseDetail]
    );

    const currentCourseProgress = selectedCourseId ? progressByCourseId[selectedCourseId] : null;
    const completedLessons = useMemo(() => {
        if (!currentCourseProgress?.videos || !Array.isArray(currentCourseProgress.videos)) return 0;
        return currentCourseProgress.videos.filter((video) => video.completed).length;
    }, [currentCourseProgress]);
    const totalLessons = courseVideos.length;
    const detailProgressPercentage = totalLessons > 0
        ? Math.round((completedLessons / totalLessons) * 100)
        : 0;

    useEffect(() => {
        const missingCourses = courses.filter((course) => course?._id && courseVideoCounts[course._id] === undefined);
        if (missingCourses.length === 0) return;

        let cancelled = false;
        Promise.all(
            missingCourses.map(async (course) => {
                try {
                    const response = await AcademyAPI.getCourse(course._id);
                    return [course._id, response?.course?.videos?.filter((video) => video.active !== false).length || 0];
                } catch {
                    return [course._id, null];
                }
            })
        ).then((entries) => {
            if (cancelled) return;
            setCourseVideoCounts((prev) => {
                const next = { ...prev };
                entries.forEach(([courseId, count]) => {
                    next[courseId] = count;
                });
                return next;
            });
        });

        return () => {
            cancelled = true;
        };
    }, [courses, courseVideoCounts]);

    useEffect(() => {
        if (view !== "detail") return;
        if (!courseVideos.length) {
            setSelectedVideo(null);
            setSelectedVideoIndex(0);
            setShowBackButton(false);
            setShowNextButton(false);
            return;
        }
        const firstYoutubeIndex = courseVideos.findIndex((video) => video.type === "youtube" && getYoutubeVideoId(video.url));
        const nextIndex = firstYoutubeIndex >= 0 ? firstYoutubeIndex : 0;
        setSelectedVideoIndex(nextIndex);
        setSelectedVideo(courseVideos[nextIndex]);
        setShowBackButton(false);
        setShowNextButton(false);
    }, [courseVideos, view]);

    const handleSelectCourse = async (courseId) => {
        if (!enrolledCourseIds.has(String(courseId))) {
            setEnrollingCourseId(courseId);
            try {
                await enroll(courseId);
                await reloadProgress();
            } catch (err) {
                console.error("Error inscribiendo al curso:", err);
                setEnrollingCourseId(null);
                return;
            } finally {
                setEnrollingCourseId(null);
            }
        }
        setSelectedCourseId(courseId);
        setSelectedVideo(null);
        setSelectedVideoIndex(0);
        setShowBackButton(false);
        setShowNextButton(false);
        setView("detail");
    };

    const getQuizTabForUser = () => {
        if (isHost) return "host-quiz";
        if (isOwner) return isMobile ? "resto-quiz" : "trivias";
        return "trivias";
    };

    const handleRedeemTrivia = async () => {
        const quizId = courseDetail?.quizId;
        console.log("[Academy] handleRedeemTrivia quizId:", quizId, "courseDetail:", courseDetail);
        if (!quizId) {
            routerHistory.push({ pathname: "/", search: `?tab=${getQuizTabForUser()}` });
            return;
        }
        setRedeemingTrivia(true);
        try {
            const tenantId = getTenantId();
            if (tenantId) {
                await addTenantTopic(tenantId, quizId);
                console.log("[Academy] addTenantTopic success");
            }
        } catch (err) {
            console.error("[Academy] Error canjeando trivia:", err);
        } finally {
            setRedeemingTrivia(false);
            localStorage.setItem("pendingQuizId", quizId);
            console.log("[Academy] saved pendingQuizId:", quizId, "navigating to tab:", getQuizTabForUser());
            routerHistory.push({ pathname: "/", search: `?tab=${getQuizTabForUser()}` });
        }
    };

    const handleBack = () => {
        setView("list");
        setSelectedCourseId(null);
        setSelectedVideo(null);
        setSelectedVideoIndex(0);
        setShowBackButton(false);
        setShowNextButton(false);
    };

    const handleVideoEnded = useCallback(async () => {
        if (!selectedCourseId || !selectedVideo?._id) return;
        try {
            await complete(selectedCourseId, selectedVideo._id);
            const currentIndex = courseVideos.findIndex((v) => v._id === selectedVideo._id);
            const hasNext = currentIndex !== -1 && currentIndex < courseVideos.length - 1;
            setShowNextButton(hasNext);
            setShowBackButton(!hasNext);
            await reloadProgress();
        } catch (err) {
            console.error("Error completando video:", err);
        }
    }, [selectedCourseId, selectedVideo, complete, reloadProgress, courseVideos]);

    // Ref para que el player de YouTube siempre llame a la versión más reciente
    useEffect(() => {
        handleVideoEndedRef.current = handleVideoEnded;
    }, [handleVideoEnded]);

    // Inicializar player de YouTube cuando sea necesario
    useEffect(() => {
        if (view !== "detail" || !selectedVideo || selectedVideo.type !== "youtube") return;
        const youtubeId = getYoutubeVideoId(selectedVideo.url);
        if (!youtubeId) return;

        const initPlayer = () => {
            if (!window.YT || !window.YT.Player) return;
            if (youtubePlayerRef.current) {
                youtubePlayerRef.current.destroy();
                youtubePlayerRef.current = null;
            }
            const player = new window.YT.Player("youtube-player", {
                videoId: youtubeId,
                playerVars: {
                    rel: 0,
                    enablejsapi: 1,
                },
                events: {
                    onStateChange: (event) => {
                        if (event.data === window.YT.PlayerState.ENDED) {
                            handleVideoEndedRef.current?.();
                        }
                    },
                },
            });
            youtubePlayerRef.current = player;
        };

        if (window.YT && window.YT.Player) {
            initPlayer();
        } else {
            const existingScript = document.getElementById("youtube-iframe-api");
            if (!existingScript) {
                const tag = document.createElement("script");
                tag.id = "youtube-iframe-api";
                tag.src = "https://www.youtube.com/iframe_api";
                const firstScriptTag = document.getElementsByTagName("script")[0];
                firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
            }
            const originalReady = window.onYouTubeIframeAPIReady;
            window.onYouTubeIframeAPIReady = () => {
                if (originalReady) originalReady();
                initPlayer();
            };
            return () => {
                window.onYouTubeIframeAPIReady = originalReady;
            };
        }

        return () => {
            if (youtubePlayerRef.current) {
                youtubePlayerRef.current.destroy();
                youtubePlayerRef.current = null;
            }
        };
    }, [view, selectedVideo]);

    const handleNextVideo = useCallback(() => {
        if (!courseVideos.length || !selectedVideo) return;
        const currentIndex = courseVideos.findIndex((v) => v._id === selectedVideo._id);
        const nextVideo = courseVideos[currentIndex + 1];
        if (nextVideo) {
            setSelectedVideoIndex(currentIndex + 1);
            setSelectedVideo(nextVideo);
            setShowBackButton(false);
            setShowNextButton(false);
        }
    }, [courseVideos, selectedVideo]);

    const handleFinishCourse = useCallback(async () => {
        setView("list");
        setSelectedCourseId(null);
        setSelectedVideo(null);
        setSelectedVideoIndex(0);
        setShowBackButton(false);
        setShowNextButton(false);
        await reloadCourses();
        await reloadProgress();
    }, [reloadCourses, reloadProgress]);

    // Render: Lista de cursos (todos los disponibles)
    if (view === "list") {
        const loading = loadingCourses || loadingProgress;
        const hasError = errorCourses || errorProgress;

        return (
            <Page>
                <Content>
                    {loading && <StatusText>Cargando cursos...</StatusText>}
                    {hasError && <StatusText $error>{errorCourses || errorProgress}</StatusText>}
                    {!loading && !hasError && (
                        <>
                            {isDesktop && <SectionTitle>AcademIA</SectionTitle>}
                            <ViewDescription>
                                Accede aquí a cursos que potenciarán tu crecimiento
                            </ViewDescription>
                            {categoryOptions.length > 0 && (
                                <QuizCategoryFilter
                                    categories={categoryOptions}
                                    selectedCategory={selectedCategory}
                                    onSelect={setSelectedCategory}
                                />
                            )}
                            <CourseAccordion
                                title="Mis cursos"
                                courses={watchedCourses}
                                courseVideoCounts={courseVideoCounts}
                                onSelectCourse={handleSelectCourse}
                                enrollingCourseId={enrollingCourseId}
                                emptyText="No has visto ningún curso."
                                defaultOpen
                            />
                            <CourseAccordion
                                title="Disponibles"
                                courses={notWatchedCourses}
                                courseVideoCounts={courseVideoCounts}
                                onSelectCourse={handleSelectCourse}
                                enrollingCourseId={enrollingCourseId}
                                emptyText="No hay cursos sin ver."
                            />
                        </>
                    )}
                </Content>
            </Page>
        );
    }

    // Render: Detalle del curso
    if (view === "detail" && courseDetail) {
        const youtubeId = selectedVideo?.type === "youtube" ? getYoutubeVideoId(selectedVideo.url) : null;

        return (
            <Page>
                <Content>
                    <BackButton onClick={handleBack}>← Volver</BackButton>
                    <DetailTop>
                        <DetailTitle>{courseDetail.title}</DetailTitle>
                        <LessonProgress>
                            {completedLessons} de {totalLessons} lecciones aprendidas
                        </LessonProgress>
                    </DetailTop>
                    {loadingDetail && <StatusText>Cargando videos...</StatusText>}
                    {errorDetail && <StatusText $error>{errorDetail}</StatusText>}
                    {selectedVideo ? (
                        <>
                            <CurrentLessonMeta>
                                Lección {selectedVideoIndex + 1} de {totalLessons}
                            </CurrentLessonMeta>
                            <PlayerWrapper>
                                {youtubeId ? (
                                    <YouTubePlayerContainer id="youtube-player" />
                                ) : (
                                    <VideoPlayer
                                        ref={videoRef}
                                        src={selectedVideo.url}
                                        controls
                                        playsInline
                                        onEnded={handleVideoEnded}
                                    />
                                )}
                            </PlayerWrapper>
                            {youtubeId && completing && (
                                <ManualComplete>
                                    <StatusText>Marcando como visto...</StatusText>
                                </ManualComplete>
                            )}
                            <ProgressSummary>
                                <ProgressCircle $percentage={detailProgressPercentage}>
                                    <ProgressCircleInner>{detailProgressPercentage}%</ProgressCircleInner>
                                </ProgressCircle>
                                <ProgressDetails>
                                    <ProgressTitle>Progreso del curso</ProgressTitle>
                                    <ProgressTrack aria-hidden="true">
                                        <ProgressTrackFill style={{ width: `${detailProgressPercentage}%` }} />
                                    </ProgressTrack>
                                    <ProgressCopy>
                                        {completedLessons} de {totalLessons} videos vistos
                                    </ProgressCopy>
                                </ProgressDetails>
                            </ProgressSummary>
                            {(showNextButton || showBackButton) && (
                                <PlayerActions>
                                    {showNextButton && (
                                        <ActionButton onClick={handleNextVideo}>
                                            Siguiente
                                        </ActionButton>
                                    )}
                                    {showBackButton && (
                                        <ActionButton onClick={handleFinishCourse}>
                                            Finalizar
                                        </ActionButton>
                                    )}
                                </PlayerActions>
                            )}
                            <DescriptionCard>
                                <DescriptionTitle>Descripción del curso</DescriptionTitle>
                                <DetailDescription>
                                    {courseDetail.description || selectedVideo.description || "Sin descripción disponible."}
                                </DetailDescription>
                            </DescriptionCard>
                        </>
                    ) : (
                        <StatusText>No hay videos disponibles en este curso.</StatusText>
                    )}

                    {/* Trivia de evaluación oculta temporalmente
                    {courseDetail?.quizId && (
                        <TriviaSection>
                            <SectionTitle>Trivia de evaluación</SectionTitle>
                            {(() => {
                                const courseProgress = progressByCourseId[courseDetail._id];
                                const isCompleted = courseProgress?.percentage === 100 || courseProgress?.completed;
                                if (!isCompleted) {
                                    return (
                                        <TriviaCardLocked>
                                            <TriviaIcon>🔒</TriviaIcon>
                                            <TriviaCardText>
                                                <strong>Trivia bloqueada</strong>
                                                <span>Completa el curso al 100% para desbloquear la trivia de evaluación.</span>
                                            </TriviaCardText>
                                        </TriviaCardLocked>
                                    );
                                }
                                return (
                                    <TriviaCardUnlocked onClick={handleRedeemTrivia}>
                                        <TriviaIcon>📝</TriviaIcon>
                                        <TriviaCardText>
                                            <strong>Trivia de evaluación</strong>
                                            <span>{redeemingTrivia ? "Canjeando trivia..." : "Pon a prueba lo aprendido en este curso."}</span>
                                        </TriviaCardText>
                                        <TriviaArrow>{redeemingTrivia ? "⏳" : "›"}</TriviaArrow>
                                    </TriviaCardUnlocked>
                                );
                            })()}
                        </TriviaSection>
                    )}
                    */}
                </Content>
            </Page>
        );
    }

    return (
        <Page>
            <Content>
                <StatusText>Cargando...</StatusText>
            </Content>
        </Page>
    );
};

export default AcademyHome;

/* ========== ACORDEÓN DE CURSOS ========== */

const CourseAccordion = ({
    title,
    courses,
    courseVideoCounts,
    onSelectCourse,
    enrollingCourseId,
    emptyText,
    defaultOpen = false,
}) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <AccordionWrapper>
            <AccordionHeader
                type="button"
                onClick={() => setIsOpen((prev) => !prev)}
                aria-expanded={isOpen}
            >
                <span>{title}</span>
                <AccordionArrow $isOpen={isOpen}>▼</AccordionArrow>
            </AccordionHeader>
            {isOpen && (
                <AccordionBody>
                    {courses.length === 0 ? (
                        <StatusText>{emptyText}</StatusText>
                    ) : (
                        <Grid>
                            {courses.map(({ course }) => {
                                const videoCount = courseVideoCounts[course._id];
                                const lessonsText = videoCount === undefined
                                    ? "Cargando lecciones..."
                                    : videoCount === null
                                        ? "Lecciones no disponibles"
                                        : `${videoCount} ${videoCount === 1 ? "video" : "videos"}`;

                                return (
                                    <Card
                                        key={course._id}
                                        onClick={() => onSelectCourse(course._id)}
                                        style={{ opacity: enrollingCourseId === course._id ? 0.6 : 1 }}
                                    >
                                        {course.thumbnail ? (
                                            <CardImage src={course.thumbnail} alt={course.title} />
                                        ) : (
                                            <CardPlaceholder>
                                                <PlaceholderImage src={playCircleIcon} alt="Curso" />
                                            </CardPlaceholder>
                                        )}
                                        <CardBody>
                                            <CardTitle>{course.title}</CardTitle>
                                            {course.category && <CardCategory>{course.category?.name || course.category}</CardCategory>}
                                            <LessonCount>{lessonsText}</LessonCount>
                                        </CardBody>
                                    </Card>
                                );
                            })}
                        </Grid>
                    )}
                </AccordionBody>
            )}
        </AccordionWrapper>
    );
};

/* ========== ESTILOS ========== */

const Page = styled.section`
  min-height: calc(100vh - 140px);
  padding: 12px 0 90px;

  @media (max-width: 768px) {
    padding: 12px 0 80px;
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 0 15px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 16px 0 12px;
  color: #111827;
`;

const StatusText = styled.p`
  margin: 12px 0;
  font-size: 14px;
  color: ${(props) => (props.$error ? "#dc2626" : "#6b7280")};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 16px;
`;

const Card = styled.div`
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.2s;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 140px;
  object-fit: cover;
  display: block;
`;

const CardPlaceholder = styled.div`
  width: 100%;
  height: 140px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlaceholderText = styled.span`
  font-size: ${(props) => (props.large ? "48px" : "32px")};
  font-weight: 700;
  color: #d1d5db;
`;

const PlaceholderImage = styled.img`
  width: 48px;
  height: 48px;
  opacity: 0.35;
`;

const CardBody = styled.div`
  padding: 12px;
`;

const CardTitle = styled.h3`
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 4px;
  color: #111827;
`;

const CardCategory = styled.span`
  font-size: 12px;
  color: #6b7280;
  text-transform: capitalize;
`;

const LessonCount = styled.p`
  margin: 10px 0 0;
  color: #374151;
  font-size: 13px;
  font-weight: 700;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  margin-top: 8px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: #10b981;
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const ProgressText = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 4px 0 0;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #111827;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 0;
  margin-bottom: 8px;

  &:hover {
    text-decoration: underline;
  }
`;

const DetailHeader = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const DetailImage = styled.img`
  width: 200px;
  height: 130px;
  object-fit: cover;
  border-radius: 12px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    height: 180px;
  }
`;

const DetailPlaceholder = styled.div`
  width: 200px;
  height: 130px;
  background: #f3f4f6;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
    height: 180px;
  }
`;

const DetailInfo = styled.div`
  flex: 1;
`;

const DetailTop = styled.div`
  display: grid;
  gap: 8px;
  margin-bottom: 16px;
`;

const DetailTitle = styled.h1`
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 8px;
  color: #111827;
`;

const LessonProgress = styled.p`
  margin: 0;
  color: ${theme.accent};
  font-size: 14px;
  font-weight: 800;
`;

const ProgressSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 16px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #ffffff;
  text-align: left;
`;

const ProgressCircle = styled.div`
  width: 64px;
  height: 64px;
  flex: 0 0 auto;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${({ $percentage }) => `conic-gradient(${theme.accent} ${$percentage * 3.6}deg, #e5e7eb 0deg)`};
`;

const ProgressCircleInner = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #ffffff;
  display: grid;
  place-items: center;
  color: #111827;
  font-size: 14px;
  font-weight: 800;
`;

const ProgressDetails = styled.div`
  flex: 1;
  min-width: 0;
  text-align: left;
`;

const ProgressTitle = styled.h3`
  margin: 0 0 10px;
  color: #111827;
  font-size: 15px;
  font-weight: 800;
  text-align: left;
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 8px;
  border-radius: 999px;
  background: #e5e7eb;
  overflow: hidden;
`;

const ProgressTrackFill = styled.div`
  height: 100%;
  border-radius: inherit;
  background: ${theme.accent};
  transition: width 0.3s ease;
`;

const ProgressCopy = styled.p`
  margin: 8px 0 0;
  color: #6b7280;
  font-size: 13px;
  font-weight: 700;
  text-align: left;
`;

const DescriptionCard = styled.div`
  margin-top: 16px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  background: #ffffff;
  text-align: left;
`;

const DescriptionTitle = styled.h3`
  margin: 0 0 10px;
  color: #111827;
  font-size: 15px;
  font-weight: 800;
  text-align: left;
`;

const DetailDescription = styled.p`
  font-size: 14px;
  color: #4b5563;
  margin: 0;
  line-height: 1.5;
  text-align: left;
`;

const CurrentLessonMeta = styled.p`
  margin: 0 0 10px;
  color: #6b7280;
  font-size: 13px;
  font-weight: 700;
`;

const DetailCategory = styled.span`
  font-size: 13px;
  color: #6b7280;
  text-transform: capitalize;
`;

const VideoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const VideoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #f9fafb;
  }
`;

const VideoNumber = styled.div`
  width: 32px;
  height: 32px;
  background: #111827;
  color: #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
`;

const VideoInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const VideoTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 2px;
  color: ${(props) => (props.completed ? "#10b981" : "#111827")};
`;

const VideoDesc = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VideoMeta = styled.p`
  font-size: 12px;
  color: #9ca3af;
  margin: 2px 0 0;
`;

const PlayIcon = styled.div`
  width: 32px;
  height: 32px;
  background: ${theme.primary};
  color: ${theme.text};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  flex-shrink: 0;
`;

const PlayerTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 12px;
  color: #111827;
`;

const PlayerWrapper = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
`;

const YouTubePlayerContainer = styled.div`
  width: 100%;
  height: 100%;
`;

const VideoPlayer = styled.video`
  width: 100%;
  height: 100%;
  display: block;
`;

const ManualComplete = styled.div`
  margin-top: 16px;
  text-align: center;
`;

const PlayerActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  border: 0;
  border-radius: 8px;
  background: #111827;
  color: #ffffff;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  padding: 10px 16px;

  &:hover {
    background: #1f2937;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const VideoDescription = styled.p`
  font-size: 14px;
  color: #4b5563;
  margin-top: 16px;
  line-height: 1.5;
`;

const ModalMessage = styled.p`
  margin: 0;
  text-align: center;
  color: #374151;
  font-size: 14px;
  line-height: 1.5;
  padding-bottom: 1rem;
`;

const TriviaSection = styled.div`
  margin-top: 24px;
`;

const TriviaCardBase = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
`;

const TriviaCardLocked = styled(TriviaCardBase)`
  background: #f9fafb;
  opacity: 0.7;
`;

const TriviaCardUnlocked = styled(TriviaCardBase)`
  background: #ffffff;
  cursor: pointer;
  transition: box-shadow 0.2s, border-color 0.2s;

  &:hover {
    border-color: ${theme.accent};
    box-shadow: 0 2px 8px ${theme.primaryTransparent};
  }
`;

const TriviaIcon = styled.span`
  font-size: 24px;
  flex-shrink: 0;
`;

const TriviaCardText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;

  strong {
    font-size: 15px;
    font-weight: 600;
    color: #111827;
  }

  span {
    font-size: 13px;
    color: #6b7280;
  }
`;

const TriviaArrow = styled.span`
  font-size: 20px;
  color: ${theme.accent};
  font-weight: 700;
`;

const AccordionWrapper = styled.div`
  margin-bottom: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  background: #ffffff;
`;

const AccordionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  background: #f9fafb;
  border: none;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  cursor: pointer;
  text-align: left;
  transition: background 0.2s ease;

  &:hover {
    background: #f3f4f6;
  }
`;

const AccordionArrow = styled.span`
  display: inline-block;
  font-size: 14px;
  color: #6b7280;
  transform: ${({ $isOpen }) => ($isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  transition: transform 0.2s ease;
`;

const AccordionBody = styled.div`
  padding: 16px;
`;

const ViewDescription = styled.p`
  margin: 0 0 20px;
  color: #6b7280;
  font-size: 15px;
  line-height: 1.5;
`;
